import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { NetworkTraceInfo } from '@atlassian/search-client';

import {
	type FullPageSearchNetworkRefs,
	type NetworkTaskState,
} from '../../common/utils/events/vital-stats-events';
import {
	createFullPageSearchNetworkVitalStats,
	createFullPageSearchVitalStats,
} from '../../common/utils/vital-stats/full-page-search-vitalstats';
import { useAnalytics } from '../../controllers/analytics';
import { useBootstrapState } from '../../controllers/store/bootstrap/accessors';

/**
 * The VitalStats API returned by the context hook.
 */
export type FullPageSearchVitalStatsAPI = {
	/**
	 * Called when a new search is initiated.
	 * Handles UX taskStart, Network taskAbort (superseded), and Network taskStart.
	 */
	onSearchStart: (options: { cacheAvailable: boolean; isNetworkMode: boolean }) => string;

	/**
	 * Called when network request succeeds.
	 */
	onNetworkSuccess: (resultCount: number, traceInfo?: NetworkTraceInfo) => void;

	/**
	 * Called when network request fails.
	 */
	onNetworkFail: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => void;

	/**
	 * Called when network request is aborted.
	 */
	onNetworkAbort: (reason: 'unmounted' | 'superseded') => void;

	/**
	 * Get the current UX task ID.
	 */
	getUxTaskId: () => string | null;

	/**
	 * Get the network refs for passing to child components that need direct access.
	 */
	getNetworkRefs: () => FullPageSearchNetworkRefs;

	/**
	 * Get the UX task ID ref for passing to child components that need direct access.
	 */
	getUxTaskIdRef: () => React.MutableRefObject<string | null>;
};

export const FullPageSearchVitalStatsContext = createContext<FullPageSearchVitalStatsAPI | null>(null);

/**
 * Provider component that encapsulates all VitalStats refs and provides a memoized API.
 *
 * This provider should wrap SearchPageLayout to make VitalStats available to all children.
 */
export const FullPageSearchVitalStatsProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { searchSessionId } = useBootstrapState((state) => state);

	// UX VitalStats task id ref (full page search)
	const uxTaskIdRef = useRef<string | null>(null);

	// Consolidated ref for Network VitalStats (taskId, startAt, state share same lifecycle)
	const fullPageSearchNetworkRefs = useRef<FullPageSearchNetworkRefs['current']>({
		taskId: null,
		startAt: 0,
		state: {
			taskId: null,
			uxTaskId: null,
			cacheAvailable: false,
			state: 'idle',
		} as NetworkTaskState,
	});

	// Refs to avoid stale closures in unmount cleanup
	const searchSessionIdRef = useRef<string | null | undefined>(undefined);
	const fireAnalyticsEventRef = useRef(fireAnalyticsEvent);

	// Keep refs in sync for unmount cleanup (avoids stale closures)
	useEffect(() => {
		searchSessionIdRef.current = searchSessionId;
	}, [searchSessionId]);

	useEffect(() => {
		fireAnalyticsEventRef.current = fireAnalyticsEvent;
	}, [fireAnalyticsEvent]);

	// Helper to create network vitalstats instance with current refs
	const getNetworkVitalStats = () =>
		createFullPageSearchNetworkVitalStats({
			searchSessionId: searchSessionIdRef.current ?? '',
			fireAnalyticsEvent: fireAnalyticsEventRef.current,
			fullPageSearchNetworkRefs,
		});

	// Network VitalStats unmount cleanup - fire taskAbort if task is in progress
	useEffect(() => {
		return () => {
			if (fg('rovo-search-vitalstats-fullpage-search-network')) {
				getNetworkVitalStats().onNetworkAbort('unmounted');
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Memoized API that doesn't change identity (prevents re-renders)
	const api = useMemo<FullPageSearchVitalStatsAPI>(() => {
		// Use a ref-based approach so the API object identity is stable
		// but always uses the latest values from refs
		return {
			onSearchStart: ({ cacheAvailable, isNetworkMode }) => {
				const vitalStats = createFullPageSearchVitalStats({
					searchSessionId: searchSessionIdRef.current,
					fireAnalyticsEvent: fireAnalyticsEventRef.current,
					fullPageSearchTaskIdRef: uxTaskIdRef,
					fullPageSearchNetworkRefs,
					cacheAvailable,
					isNetworkMode,
				});
				return vitalStats.onSearchStart();
			},

			onNetworkSuccess: (resultCount: number, traceInfo?: NetworkTraceInfo) => {
				getNetworkVitalStats().onNetworkSuccess(resultCount, traceInfo);
			},

			onNetworkFail: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => {
				getNetworkVitalStats().onNetworkFail(errorName, errorMessage, traceInfo);
			},

			onNetworkAbort: (reason: 'unmounted' | 'superseded') => {
				getNetworkVitalStats().onNetworkAbort(reason);
			},

			getUxTaskId: () => uxTaskIdRef.current,

			getNetworkRefs: () => fullPageSearchNetworkRefs,

			getUxTaskIdRef: () => uxTaskIdRef,
		};
	}, []);

	return (
		<FullPageSearchVitalStatsContext.Provider value={api}>
			{children}
		</FullPageSearchVitalStatsContext.Provider>
	);
};

/**
 * Hook to access the FullPageSearchVitalStats API from context.
 *
 * @throws Error if used outside of FullPageSearchVitalStatsProvider
 */
export const useFullPageSearchVitalStats = (): FullPageSearchVitalStatsAPI => {
	const context = useContext(FullPageSearchVitalStatsContext);
	if (!context) {
		throw new Error(
			'useFullPageSearchVitalStats must be used within a FullPageSearchVitalStatsProvider',
		);
	}
	return context;
};

